/*
 * Surgical Suite — Public Share Viewer.
 * Opens at /surgical-share/{token}. No login required — the long-random
 * share token is the auth. Displays the sealed discharge summary, a "Open PDF"
 * button and a "Print" button. If the link is revoked / expired the page
 * shows a friendly notice.
 */
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AlertTriangle, FileDown, Loader2, Printer, Stethoscope } from 'lucide-react';
import api from '@/lib/api';

const NAVY = '#07122f';
const TEAL = '#007f73';

export default function SurgicalSharedDischargePage() {
  const { token } = useParams();
  const [payload, setPayload] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);
  const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/public/surgical-discharge/${token}/pdf`;

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/public/surgical-discharge/${token}`);
        if (alive) setPayload(data);
      } catch (e) {
        if (alive) setErr(e?.response?.data?.detail || 'Share link unavailable');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [token]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-slate-500" /></div>;
  if (err) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="bg-white border border-rose-200 rounded-2xl p-6 max-w-md text-center">
        <AlertTriangle size={32} className="text-rose-500 mx-auto mb-2" />
        <div className="text-lg font-bold" style={{ color: NAVY }}>Cannot open share link</div>
        <div className="text-sm text-slate-600 mt-1">{err}</div>
        <Link to="/" className="mt-4 inline-block text-teal-700 text-sm font-semibold">Return to home</Link>
      </div>
    </div>
  );

  const d = payload?.discharge || {};
  return (
    <div className="min-h-screen bg-slate-100 print:bg-white">
      <div className="max-w-4xl mx-auto p-6 print:p-0">
        <div className="bg-white rounded-2xl border border-slate-200 p-6 print:border-0 print:shadow-none">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: NAVY }}>
              <Stethoscope size={20} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-bold tracking-[.15em] text-teal-700 uppercase">Shared with {payload?.recipient_name}</div>
              <h1 className="text-lg font-black truncate" style={{ color: NAVY }}>{d.hospital_name || 'Hospital'} — Post-Op Discharge Summary</h1>
              <div className="text-[11px] text-slate-500">Shared by {payload?.shared_by} · Link expires {String(payload?.expires_at || '').slice(0, 10)}</div>
            </div>
          </div>

          {payload?.shared_note && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm text-amber-900">
              <b>Sender's note:</b> {payload.shared_note}
            </div>
          )}

          <div className="flex flex-wrap gap-2 mb-5 print:hidden">
            <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-[13px] font-semibold text-white" style={{ background: NAVY }} data-testid="surg-share-pdf">
              <FileDown size={14} /> Open PDF
            </a>
            <button type="button" onClick={() => window.print()} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-[13px] font-semibold border border-slate-300 bg-white hover:bg-slate-50" data-testid="surg-share-print">
              <Printer size={14} /> Print
            </button>
          </div>

          <Section label="Patient">{d.patient_name || '—'} · {d.patient_sex || '—'} · {d.patient_age || '—'} · CR {d.patient_cr_no || '—'}</Section>
          <Section label="Admitted Under">{d.principal_surgeon_name || '—'}</Section>
          <Section label="Diagnosis">{d.diagnosis || '—'}</Section>
          <Section label="Operation Performed">{d.operation_performed || '—'}</Section>
          <SectionList label="Operative Findings" items={d.operative_findings} />
          <Section label="Post-Operative Plan">{d.post_op_plan || '—'}</Section>
          <SectionList label="Treatments Advised" items={d.treatments_advised} />
          <SectionList label="Follow-up Schedule" items={(d.followup_schedule || []).map((f) => `${f.date || ''} — ${f.action || ''}`)} />
        </div>
      </div>
    </div>
  );
}

const Section = ({ label, children }) => (
  <div className="mb-3">
    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
    <div className="text-sm text-slate-900 whitespace-pre-wrap" style={{ color: NAVY }}>{children}</div>
  </div>
);
const SectionList = ({ label, items }) => (
  <div className="mb-3">
    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
    {(!items || items.length === 0) ? <div className="text-sm text-slate-400 italic">—</div> : (
      <ul className="list-disc pl-5 text-sm" style={{ color: NAVY }}>
        {items.map((x, i) => <li key={i}>{x}</li>)}
      </ul>
    )}
  </div>
);
